import { Typography } from "@mui/material";
import Paper from "@components/Paper/Paper";
import PaperItem from "@components/Paper/PaperItem";
import DocumentCard from "@features/vessel-nomination/nomination-details/components/DocumentCard/DocumentCard";
import { useDownloadNominationDocument } from "@features/vessel-nomination/nomination-details/hooks/useDownloadNominationDocument";
import type { VesselNominationDetailsResponse } from "@services/vessel-nomination/types";
import { convertUtcToUae, formatDateToDDMMYYYY } from "@utils/date-time";
import { useTranslation } from "react-i18next";
import ParcelsTable from "../components/table/ParcelsTable";
import { LANG_BASE, TEST_ID_BASE } from "../VesselNominationDetails";

interface FirstRequestDetailsProps {
  nominationDetails?: VesselNominationDetailsResponse;
}

const displayValue = (value: unknown) =>
  value === null || value === undefined || value === "" ? "-" : String(value);

const FirstRequestDetails = ({ nominationDetails }: FirstRequestDetailsProps) => {
  const { t } = useTranslation();
  const { handleDownload, handlePreview } = useDownloadNominationDocument();
  const remarks = nominationDetails?.remarks?.trim();
  const documents = nominationDetails?.documents ?? [];
  const eta = nominationDetails?.eta ?? nominationDetails?.rta;
  const details = [
    {
      label: t(`${LANG_BASE}.first-request.eta`),
      value: eta ? formatDateToDDMMYYYY(convertUtcToUae(eta), true) : "-",
    },
    { label: t(`${LANG_BASE}.first-request.call-sign`), value: nominationDetails?.callSign },
    {
      label: t(`${LANG_BASE}.first-request.summer-dwt`),
      value: nominationDetails?.summerDwt ?? nominationDetails?.deadWeight?.summerDwt,
    },
    {
      label: t(`${LANG_BASE}.first-request.arrival-draft`),
      value: nominationDetails?.arrivalDraft,
    },
    {
      label: t(`${LANG_BASE}.first-request.departure-draft`),
      value: nominationDetails?.departureDraft,
    },
    {
      label: t(`${LANG_BASE}.first-request.hull-protrusion`),
      value:
        nominationDetails?.hasHullProtrusion === undefined
          ? "-"
          : t(nominationDetails.hasHullProtrusion ? "common.yes" : "common.no"),
    },
  ];

  return (
    <div data-testid={`${TEST_ID_BASE}-first-request`} className="flex flex-col gap-6">
      <Paper variant="outlined">
        <Typography variant="body2" sx={{ marginBottom: 3 }}>
          {t(`${LANG_BASE}.first-request.title`)}
        </Typography>
        <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
          {details.map((detail) => (
            <PaperItem
              key={detail.label}
              translate={detail.label}
              text={displayValue(detail.value)}
              wrapperStyle="border-b border-[#F1F2F4] pb-3 mb-3 flex gap-4"
              tranStyle="font-normal text-[#646A77]"
              textStyles="font-normal"
            />
          ))}
        </div>
      </Paper>

      <section>
        <Typography variant="body2" sx={{ marginBottom: 2 }}>
          {t(`${LANG_BASE}.first-request.cargo-title`)}
        </Typography>
        <ParcelsTable rows={nominationDetails?.parcels} />
      </section>

      {remarks && (
        <Paper variant="outlined">
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {t(`${LANG_BASE}.first-request.remarks`)}
          </Typography>
          <div>{remarks}</div>
        </Paper>
      )}

      {documents.length > 0 && (
        <section data-testid={`${TEST_ID_BASE}-first-request-documents`}>
          <Typography variant="body2" sx={{ marginBottom: 2 }}>
            {t(`${LANG_BASE}.first-request.attachments`)}
          </Typography>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {documents.map((document, index) => (
              <DocumentCard
                key={`${document.fileUrl}-${index}`}
                testId={`first-request-document-${index}`}
                documentName={document.documentName}
                documentType={document.documentType}
                otherSpecify={document.otherSpecify ?? document.otherDocumentType}
                documentSummary={document.documentSummary}
                issueDate={document.issueDate}
                expiryDate={document.expiryDate}
                updateDate={document.updatedAt}
                onPreview={() => handlePreview("bff-vessel", document.fileUrl)}
                onDownload={() =>
                  handleDownload("bff-vessel", document.fileUrl, document.documentName)
                }
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default FirstRequestDetails;
